"use client";

import { nanoid } from "nanoid";
import {
	isAiDefaultEntryPath,
	isAiLowSignalPath,
} from "@/lib/ai/browsing-rules";
import { parseAiRouteMeta } from "@/lib/ai/entity-route";
import {
	AI_BROWSING_STORE,
	promisifyRequest,
	withStore,
} from "@/lib/ai/storage/indexeddb";
import type { AiBrowsingEvent } from "@/lib/ai/storage/types";

const AI_BROWSING_RETENTION_MS = 1000 * 60 * 60 * 24 * 7;
const AI_BROWSING_DEDUPE_MS = 1000 * 45;

export async function recordAiBrowsingEvent(
	event: Omit<AiBrowsingEvent, "id" | "timestamp" | "routeGroup" | "entityType" | "entityId"> & {
		timestamp?: number;
	},
) {
	const timestamp = event.timestamp ?? Date.now();
	const routeMeta = parseAiRouteMeta(event.pathname);

	await pruneAiBrowsingEvents();

	return withStore(AI_BROWSING_STORE, "readwrite", async (store) => {
		const all = (await promisifyRequest(store.getAll())) as AiBrowsingEvent[];
		const latestSamePath = all
			.filter((item) => item.pathname === event.pathname)
			.sort((a, b) => b.timestamp - a.timestamp)[0];

		if (latestSamePath && timestamp - latestSamePath.timestamp < AI_BROWSING_DEDUPE_MS) {
			const next: AiBrowsingEvent = {
				...latestSamePath,
				label: event.label,
				source: event.source,
				title: event.title ?? latestSamePath.title,
				timestamp,
				isDefaultEntry: isAiDefaultEntryPath(event.pathname),
				isLowSignal: isAiLowSignalPath(event.pathname),
				...routeMeta,
			};
			store.put(next);
			return next;
		}

		const next: AiBrowsingEvent = {
			id: nanoid(),
			pathname: event.pathname,
			label: event.label,
			source: event.source,
			title: event.title,
			timestamp,
			isDefaultEntry: isAiDefaultEntryPath(event.pathname),
			isLowSignal: isAiLowSignalPath(event.pathname),
			...routeMeta,
		};
		store.put(next);
		return next;
	});
}

export async function listRecentAiBrowsingEvents(limit = 12) {
	const minTimestamp = Date.now() - AI_BROWSING_RETENTION_MS;
	return withStore(AI_BROWSING_STORE, "readonly", async (store) => {
		const all = (await promisifyRequest(store.getAll())) as AiBrowsingEvent[];
		return all
			.filter((item) => item.timestamp >= minTimestamp)
			.sort((a, b) => b.timestamp - a.timestamp)
			.slice(0, limit);
	});
}

export async function pruneAiBrowsingEvents() {
	const minTimestamp = Date.now() - AI_BROWSING_RETENTION_MS;
	return withStore(AI_BROWSING_STORE, "readwrite", async (store) => {
		const all = (await promisifyRequest(store.getAll())) as AiBrowsingEvent[];
		for (const item of all) {
			if (item.timestamp < minTimestamp) {
				store.delete(item.id);
			}
		}
	});
}
